import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MicIcon from '@mui/icons-material/Mic';
import { FaWaveSquare } from 'react-icons/fa';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import 'css/shippingMedia.css';

const source = file => file.datafile?.startsWith('data:') ? file.datafile : `data:${file.type};base64,${file.datafile}`;
const bytes = file => Math.ceil((file.datafile?.split(',').pop()?.length || 0) * 3 / 4);

export default function ShippingMedia({ files = [], onChange, onBusyChange, disabled = false }) {
  const [expanded, setExpanded] = useState(true);
  const [camera, setCamera] = useState(false);
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const video = useRef(null);
  const stream = useRef(null);
  const recorder = useRef(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const current = useRef({ files, onChange });
  current.current = { files: files || [], onChange };
  const stopTracks = () => { stream.current?.getTracks().forEach(track => track.stop()); stream.current = null; };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
      if (recorder.current?.state === 'recording') { recorder.current.onstop = null; recorder.current.stop(); }
      stopTracks();
    };
  }, []);
  useEffect(() => { onBusyChange?.(busy || recording || camera); }, [busy, recording, camera, onBusyChange]);
  useEffect(() => {
    if (!camera) return undefined;
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(media => {
        if (cancelled) { media.getTracks().forEach(track => track.stop()); return; }
        stream.current = media;
        if (video.current) video.current.srcObject = media;
      }).catch(() => { if (!cancelled) { setError('No se pudo acceder a la cámara. Comprueba los permisos.'); setCamera(false); } });
    return () => { cancelled = true; stopTracks(); setReady(false); };
  }, [camera]);
  useEffect(() => {
    setElapsed(0);
    if (!recording) return undefined;
    const started = Date.now();
    const interval = setInterval(() => setElapsed(Math.min(60, Math.floor((Date.now() - started) / 1000))), 1000);
    return () => clearInterval(interval);
  }, [recording]);
  const add = file => {
    if (!alive.current) return;
    const existing = current.current.files;
    if (existing.length >= 5 || existing.reduce((total, entry) => total + bytes(entry), 0) + bytes(file) > 20000000) {
      setError('Máximo 5 archivos y 20 MB en total.'); return;
    }
    current.current.onChange([...existing, { id: null, shipping_id: null, createdate: new Date().toISOString(), ...file }]);
  };
  const takePhoto = () => {
    if (!video.current?.videoWidth) return;
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 1920 / video.current.videoWidth);
    canvas.width = video.current.videoWidth * scale;
    canvas.height = video.current.videoHeight * scale;
    canvas.getContext('2d').drawImage(video.current, 0, 0, canvas.width, canvas.height);
    add({ filename: `foto-${Date.now()}.jpg`, type: 'image/jpeg', datafile: canvas.toDataURL('image/jpeg', 0.85) });
    setCamera(false);
  };
  const stopAudio = () => {
    clearTimeout(timer.current);
    if (recorder.current?.state === 'recording') recorder.current.stop();
    stopTracks();
  };
  const startAudio = async () => {
    setError(''); setBusy(true);
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!alive.current) { media.getTracks().forEach(track => track.stop()); return; }
      stream.current = media;
      const mime = ['audio/mp4', 'audio/webm', 'audio/ogg'].find(type => MediaRecorder.isTypeSupported(type));
      const capture = new MediaRecorder(media, mime ? { mimeType: mime } : undefined);
      recorder.current = capture;
      const chunks = [];
      capture.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
      capture.onstop = () => {
        stopTracks(); clearTimeout(timer.current);
        if (!alive.current) return;
        setRecording(false); setBusy(true);
        const type = capture.mimeType || chunks[0]?.type || 'audio/webm';
        const reader = new FileReader();
        reader.onload = () => {
          add({ filename: `audio-${Date.now()}.${type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm'}`, type, datafile: reader.result });
          if (alive.current) setBusy(false);
        };
        reader.onerror = () => { if (alive.current) { setError('No se pudo guardar la grabación.'); setBusy(false); } };
        reader.readAsDataURL(new Blob(chunks, { type }));
      };
      capture.start(); setRecording(true); setBusy(false);
      timer.current = setTimeout(stopAudio, 60000);
    } catch (e) {
      stopTracks();
      if (alive.current) { setBusy(false); setError('No se pudo acceder al micrófono. Comprueba los permisos del navegador.'); }
    }
  };
  const blocked = disabled || busy || recording || camera || (files || []).length >= 5;
  return <section className="shipping-media" aria-label="Fotos y audio de la carta de portes">
    <div className="shipping-media-heading"><span>FOTOS Y AUDIO</span><small>Máximo 5 archivos · 20 MB</small></div>
    {error && <Alert severity="warning" onClose={() => setError('')}>{error}</Alert>}
    <div className="shipping-media-toolbar">
    <div className="shipping-media-actions">
      <Button type="button" variant="contained" startIcon={<CameraAltIcon />} disabled={blocked} onClick={() => {
        if (!navigator.mediaDevices?.getUserMedia) { setError('La cámara necesita HTTPS o localhost y un navegador compatible.'); return; }
        setError(''); setCamera(true);
      }}>TOMAR FOTO</Button>
      <Button type="button" className={recording ? "shipping-audio-recording" : undefined} variant={recording ? "contained" : "outlined"} color={recording ? 'error' : 'success'} startIcon={recording ? <FaWaveSquare className="shipping-recording-wave" /> : <MicIcon />} disabled={disabled || busy || camera || (!recording && files.length >= 5)} onClick={recording ? stopAudio : startAudio}>{recording ? 'DETENER GRABACIÓN' : 'GRABAR AUDIO'}</Button>
      {recording && <div className="shipping-recording-progress">
        <span role="status">Grabando…</span><small>{elapsed} / 60 s</small>
        <LinearProgress variant="determinate" value={elapsed / 60 * 100} aria-label="Progreso de grabación" />
      </div>}
    </div>
    <IconButton className="shipping-media-toggle" aria-label={expanded ? 'Plegar fotos y audio' : 'Desplegar fotos y audio'} aria-expanded={expanded} aria-controls="shipping-media-gallery" onClick={() => setExpanded(value => !value)}><ExpandMoreIcon /></IconButton>
    </div>
    <Collapse in={expanded} unmountOnExit>
      <div id="shipping-media-gallery">
    <div className="shipping-media-gallery">
      {!(files || []).length && <span className="shipping-media-empty">Aquí aparecerán las fotos y grabaciones de la carta de portes</span>}
      {(files || []).map((file, index) => <div className={`shipping-media-item${file.type?.startsWith('image/') ? ' shipping-media-image-item' : ''}`} key={file.id ?? index}>
        {file.type?.startsWith('image/') ? <button type="button" className="shipping-media-photo" aria-label={`Ampliar ${file.filename}`} onClick={() => setPreview(file)}><img src={source(file)} alt={file.filename} /><span className="shipping-media-zoom" aria-hidden="true">🔍</span></button>
          : <audio controls preload="metadata" src={source(file)} aria-label={file.filename} />}
        
        <IconButton className="shipping-media-delete" aria-label={`Eliminar ${file.filename}`} disabled={disabled || recording || busy} onClick={() => onChange(files.filter((_, i) => i !== index))}><DeleteIcon /></IconButton>
      </div>)}
    </div>
      </div>
    </Collapse>
    <Dialog open={camera} onClose={() => setCamera(false)} fullWidth maxWidth="sm" PaperProps={{ className: "shipping-camera-dialog" }}>
      <DialogTitle>Tomar foto</DialogTitle><DialogContent><video ref={video} autoPlay muted playsInline onLoadedMetadata={() => setReady(true)} style={{ width: '100%', borderRadius: 12 }} /></DialogContent>
      <DialogActions className="shipping-camera-actions"><Button className="shipping-camera-cancel" startIcon={<CloseRoundedIcon />} onClick={() => setCamera(false)}>Cancelar</Button><Button className="shipping-camera-capture" startIcon={<CameraAltIcon />} disabled={!ready} onClick={takePhoto} variant="contained">Capturar foto</Button></DialogActions>
    </Dialog>
    <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="md" aria-label="Foto ampliada" PaperProps={{ className: 'shipping-photo-preview' }}>
      <IconButton className="shipping-photo-close" aria-label="Cerrar foto ampliada" onClick={() => setPreview(null)}><CloseRoundedIcon /></IconButton>
      <DialogContent>{preview && <img src={source(preview)} alt={preview.filename} />}</DialogContent>
    </Dialog>
  </section>;
}
